import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Spin } from 'antd'

import { ColourButton } from 'portal/lib/primitives'
import { useModuleIds } from 'portal/lib/hooks'

import { trackMixpanel } from 'portal/store/mixpanel/actions'
import {
  loadOneClickUnexportedRecordIds,
  getOneClickReportsBeingProcessed
} from 'portal/store/records/byModule/actions'
import { queueOneClickExport } from 'portal/store/records/exports/actions'

import { createFilterDescription } from './OneClickHelpers'

export function OneClickExport({ filters, onSuccess, setIsOCEModalOpen }) {
  const { oceWorking } = useSelector(state => state.records.exports)
  const { modules } = useSelector(state => state.projectDetails)

  const projectName = modules.name

  //  We clean these values by:
  // replacing spaces . , & / with _
  // the remove any special characters except ( ) + - _
  // finally, replace 2 or more _ with a single _
  const cleanString = str => {
    return str
      .replace(/[\s.,'&/]/g, '_')
      .replace(/[^\w()+-]/gi, '')
      .replace(/_{2,}/g, '_')
  }
  const projectNameForFile = projectName ? cleanString(projectName) : ''

  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()

  const recordExportLimit = 1000

  const {
    allRecordIds,
    count,
    unexportedRecordIds,
    retrievingReports,
    reportsBeingGenerated
  } = useSelector(state => state.records.byModule[moduleName.toLowerCase()])

  const { endDate, startDate } = useSelector(
    state => state.global.dateRange.global
  )

  useEffect(() => {
    if (allRecordIds && allRecordIds.length > 0) {
      dispatch(
        loadOneClickUnexportedRecordIds(
          accountId,
          projectId,
          moduleName,
          allRecordIds
        )
      )
    }
  }, [accountId, allRecordIds, dispatch, moduleName, onSuccess, projectId])

  const OCE_ALL = 'frontend_records_ocl_export_all'
  const trackOCEAll = useCallback(() => {
    dispatch(
      trackMixpanel(OCE_ALL, {
        record_type: moduleName,
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, moduleName, projectId])

  const OCE_SELECTION = 'frontend_records_ocl_export_selection'
  const trackOCESelection = useCallback(() => {
    dispatch(
      trackMixpanel(OCE_SELECTION, {
        record_type: moduleName,
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, moduleName, projectId])

  const handleOCECancel = () => {
    setIsOCEModalOpen(false)
  }

  const handleExportAll = useCallback(() => {
    const title = `OneClick_all-${count}_${projectNameForFile}_${startDate.substr(
      0,
      10
    )}_${endDate.substr(0, 10)}.xlsx`
    const filterDescription = createFilterDescription(filters)

    dispatch(
      queueOneClickExport(
        accountId,
        projectId,
        allRecordIds,
        title,
        filterDescription,
        {
          onSuccess
        }
      )
    )
    trackOCEAll()
  }, [
    count,
    projectNameForFile,
    startDate,
    endDate,
    filters,
    dispatch,
    accountId,
    projectId,
    allRecordIds,
    onSuccess,
    trackOCEAll
  ])

  const handleExportSome = useCallback(() => {
    const title = `OneClick_partial-${
      unexportedRecordIds.length
    }_${projectNameForFile}_${startDate.substr(0, 10)}_${endDate.substr(0, 10)}.xlsx`
    const filterDescription = createFilterDescription(filters)

    dispatch(
      queueOneClickExport(
        accountId,
        projectId,
        unexportedRecordIds,
        title,
        filterDescription,
        {
          onSuccess
        }
      )
    )

    trackOCESelection()
  }, [
    unexportedRecordIds,
    projectNameForFile,
    startDate,
    endDate,
    filters,
    dispatch,
    accountId,
    projectId,
    onSuccess,
    trackOCESelection
  ])

  useEffect(() => {
    const checkReportsBeingGenerated = () => {
      dispatch(
        getOneClickReportsBeingProcessed(accountId, projectId, moduleName)
      )
    }
    checkReportsBeingGenerated()
  }, [accountId, dispatch, moduleName, projectId])

  if (!unexportedRecordIds) {
    return null
  }

  return (
    <Modal
      title="One Click LCA Export"
      open={true}
      onCancel={handleOCECancel}
      transitionName=""
      footer={
        retrievingReports || reportsBeingGenerated.length > 0 ? (
          <p></p>
        ) : (
          [
            <ColourButton.Action
              key="submit"
              type="button"
              onClick={handleExportAll}
              style={{ display: 'inline-block', marginRight: '.75rem' }}
              disabled={
                !allRecordIds || allRecordIds.length > recordExportLimit
              }
              title={
                allRecordIds && allRecordIds.length > recordExportLimit
                  ? `Export is limited to ${recordExportLimit} records`
                  : ''
              }
            >
              Export All Records
            </ColourButton.Action>,
            <ColourButton.Basic
              key="back"
              type="button"
              onClick={handleExportSome}
              style={{ display: 'inline-block' }}
              disabled={
                oceWorking ||
                unexportedRecordIds.length < 1 ||
                unexportedRecordIds.length > recordExportLimit
              }
              title={
                unexportedRecordIds.length > recordExportLimit
                  ? `Export is limited to ${recordExportLimit} records`
                  : ''
              }
            >
              Export New Records
            </ColourButton.Basic>
          ]
        )
      }
    >
      {retrievingReports ? (
        <div style={{ textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      ) : (
        <div>
          {reportsBeingGenerated.length > 0 ? (
            <div>
              <p>
                Report {reportsBeingGenerated[0].name} is currently being
                generated.
              </p>
              <p>Please wait and try again later.</p>
            </div>
          ) : (
            <div>
              <p>
                Previously you have exported{' '}
                {count - unexportedRecordIds.length} records.
              </p>
              <p>
                There are {unexportedRecordIds.length} new records that have not
                been exported yet.
              </p>
              {unexportedRecordIds.length > recordExportLimit && (
                <p>
                  Please reduce the records selected using the filters to below
                  1,000 results
                </p>
              )}
              {unexportedRecordIds.length <= recordExportLimit &&
                allRecordIds.length > recordExportLimit && (
                  <p>
                    Please reduce the records selected using the filters to
                    below 1,000 results to perform an all records export
                  </p>
                )}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
